import { useState } from 'react';
import { medicationCreate, medicationServiceLevel, type ApiClient, type ApiError, type MedicationRow, type MedServiceLevel } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { ErrorNote } from '../../ErrorNote';

const IN = 'box-sizing:border-box;height:32px;padding:0 10px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;min-width:0;';
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** How an order is given (medications.service_level), in plain words. */
export const SERVICE_LEVEL_OPTIONS: readonly { value: MedServiceLevel; label: string }[] = [
  { value: 'administer', label: 'Administer: staff give it' },
  { value: 'assistance', label: 'Assistance: staff assist, the resident takes it' },
  { value: 'self_administered', label: 'Self-administered: the resident takes it on their own' },
  { value: 'third_party', label: 'Third party: given by someone outside the facility' },
];

/**
 * Add a medication to the resident's record, with the dose times that put it on the
 * med pass (POST /medications {times}), and how each order is given (its service level:
 * PUT /medications/:id/service-level). Staff of the building only; the server checks, and
 * only the Facility Admin or a licensed nurse may set a service level other than Administer.
 */
export function MedicationAdd({ client, residentId, organizationId, readOnly, meds = [], onAdded }: {
  client: ApiClient; residentId: string; organizationId: string | null; readOnly: boolean; meds?: MedicationRow[]; onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [f, setF] = useState<{ name: string; dose: string; freq: string; times: string; level: MedServiceLevel }>({ name: '', dose: '', freq: 'Once daily', times: '08:00', level: 'administer' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  if (readOnly || !organizationId) return null;
  const times = f.times.split(/[\s,]+/).filter(Boolean);
  const ok = f.name.trim().length > 1 && times.length > 0 && times.every((t) => HHMM.test(t));
  const save = async () => {
    if (!ok || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await medicationCreate(client, {
        body: {
          userId: residentId, organizationId, name: f.name.trim(), dosage: f.dose.trim() || undefined, frequency: f.freq.trim() || undefined, times,
          ...(f.level !== 'administer' ? { serviceLevel: f.level } : {}),
        },
      });
      setF({ name: '', dose: '', freq: 'Once daily', times: '08:00', level: 'administer' });
      setOpen(false);
      onAdded();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const setLevel = async (medicationId: string, serviceLevel: MedServiceLevel) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await medicationServiceLevel(client, { params: { id: medicationId }, body: { serviceLevel } });
      onAdded();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const levels = levelsOpen && meds.length > 0 && (
    <div data-testid="medication-levels" style={sx('display:flex;flex-direction:column;gap:6px;margin-top:10px;')}>
      <span style={sx('font-size:14px;color:#54606C;line-height:1.45;')}>How each medicine is given decides who may record it. The Facility Admin or a licensed nurse sets it.</span>
      {meds.map((m) => (
        <label key={m.id} style={sx('display:flex;flex-wrap:wrap;gap:8px;align-items:center;font-size:16px;color:#22272E;')}>
          <span style={sx('flex:1 1 160px;min-width:0;font-weight:600;')}>{m.name}</span>
          <select
            aria-label={`How ${m.name} is given`}
            value={m.service_level ?? 'administer'}
            disabled={busy}
            onChange={(e) => void setLevel(m.id, e.target.value as MedServiceLevel)}
            style={sx(IN + 'flex:2 1 220px;')}
          >
            {SERVICE_LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      ))}
    </div>
  );
  if (!open) {
    return (
      <div>
        <div style={sx('display:flex;flex-wrap:wrap;gap:8px;')}>
          <button type="button" onClick={() => setOpen(true)} style={sx('margin-top:10px;display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;border:1px solid rgba(47,92,151,.45);background:transparent;color:#264C7E;font-size:16px;font-weight:600;cursor:pointer;')}>
            + Add medication
          </button>
          {meds.length > 0 && (
            <button type="button" aria-expanded={levelsOpen} onClick={() => setLevelsOpen((v) => !v)} style={sx('margin-top:10px;display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;border:1px solid rgba(34,39,46,.2);background:transparent;color:#22272E;font-size:16px;font-weight:600;cursor:pointer;')}>
              How each medicine is given
            </button>
          )}
        </div>
        {levels}
        {err && <ErrorNote error={err} />}
      </div>
    );
  }
  return (
    <div data-testid="medication-add" style={sx('display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-top:10px;')}>
      <input aria-label="Medication" placeholder="Medication" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} style={sx(IN + 'flex:2 1 160px;')} />
      <input aria-label="Dose" placeholder="Dose, e.g. 5 mg" value={f.dose} onChange={(e) => setF({ ...f, dose: e.target.value })} style={sx(IN + 'flex:1 1 110px;')} />
      <input aria-label="How often" placeholder="How often" value={f.freq} onChange={(e) => setF({ ...f, freq: e.target.value })} style={sx(IN + 'flex:1 1 110px;')} />
      <input aria-label="Dose times" placeholder="08:00, 20:00" value={f.times} onChange={(e) => setF({ ...f, times: e.target.value })} style={sx(IN + 'flex:1 1 110px;')} />
      <select aria-label="How it is given" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value as MedServiceLevel })} style={sx(IN + 'flex:2 1 220px;')}>
        {SERVICE_LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button type="button" disabled={!ok || busy} onClick={() => void save()} style={sx('height:32px;padding:0 14px;border-radius:8px;border:0;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;cursor:pointer;' + (!ok || busy ? 'opacity:.5;cursor:default;' : ''))}>
        Save medication
      </button>
      <button type="button" onClick={() => setOpen(false)} style={sx('height:32px;padding:0 10px;border:0;background:transparent;color:#3C454F;font-size:16px;cursor:pointer;')}>
        Cancel
      </button>
      {err && <ErrorNote error={err} />}
    </div>
  );
}
